if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.preventResize = false;
      this.header = document.querySelector('.section-header');
      this.headerTop = document.querySelector('.header-top');
      this.predictiveSearch = this.querySelector('predictive-search');
      this.onScrollHandler = this.onScroll.bind(this);
      this.isToggleNav = this.classList.contains('toggle-nav');
      this.setupWindowResizeEvent();
      this.calculateThreshold();
      this.hideHeaderOnScrollUp = () => this.preventReveal = true;
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      if(this.isToggleNav) {
        this.header.classList.add('toggle-nav');
        this.toggleNavButton = this.querySelector('.header__toggle-nav-button');
        this.onToggleNavClickHandler = this.onToggleNavClick.bind(this);
        this.toggleNavButton.addEventListener('click', this.onToggleNavClickHandler);
        this.nav = this.querySelector('.header__inline-menu');
        setTimeout(() => {
          document.body.style.setProperty('--nav-height', `${this.nav.clientHeight}px`);
        }, 500);
      }

      this.onScroll();

      window.addEventListener('scroll', this.onScrollHandler, false);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScrollHandler);
      window.removeEventListener('resize', this.debouncedOnWindowResize);
      if(this.toggleNavButton) {
        this.toggleNavButton.removeEventListener('click', this.onToggleNavClickHandler);
      }
    }

    calculateHeaderHeight() {
      this.headerHeight = 0;
      this.fullHeaderHeight = 0;
      this.querySelectorAll(':scope > *').forEach(element => {
        this.fullHeaderHeight += element.clientHeight;
        if(element.classList.contains('header')) {
          this.headerHeight = element.clientHeight;
          this.fullHeaderHeight += parseInt(window.getComputedStyle(element, null).getPropertyValue('margin-top'));
        }
      });
      return this.fullHeaderHeight
    }

    calculateThreshold() {
      this.headerBoundTop = this.header.getBoundingClientRect().top + (window.pageYOffset || document.documentElement.scrollTop) + this.fullHeaderHeight;
      this.threshold = this.headerBoundTop + 100;
      if(this.dataset.stickyType == 'always') {
        this.threshold += 100;
      }
    }

    onScroll() {
      const scrollTop = document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      if(scrollTop > this.currentScrollTop & scrollTop > this.headerBoundTop) {
        // Scrolling down and passed the header: hide the sticky header but keep position fixed.
        if(this.dataset.stickyType == 'always') {
          this.openStickyHeader(scrollTop);
        } else {
          if (this.preventHide || this.dataset.stickyType == 'always') return;
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBoundTop) {
        // Scrolling up and passed the header: show the sticky header.
        this.openStickyHeader(scrollTop);
      } else if (scrollTop <= this.headerBoundTop) {
        // Scroll above the header: reset the sticky header.
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    openStickyHeader(scrollTop) {
      if(this.preventOpen) return;
      if (!this.preventReveal && scrollTop > this.threshold) {
        requestAnimationFrame(this.reveal.bind(this));
      } else {
        window.clearTimeout(this.isScrolling);

        this.isScrolling = setTimeout(() => {
          this.preventReveal = false;
        }, 66);

        requestAnimationFrame(this.hide.bind(this));
      }
    }

    setupWindowResizeEvent() {
      this.calculateHeaderHeight();
      document.body.style.setProperty('--header-height', `${this.headerHeight}px`);
      if(window.innerWidth >= 990) {
        document.body.style.setProperty('--sticky-header-height-desktop', `${this.fullHeaderHeight}px`);
      } else {
        document.body.style.setProperty('--sticky-header-height-mobile', `${this.fullHeaderHeight}px`);
      }
      this.debouncedOnWindowResize = debounce((event) => {
        this.onWindowResize(event);
      }, 500);

      window.addEventListener('resize', this.debouncedOnWindowResize, false);
    }

    onWindowResize() {
      if(this.preventResize) return;
      let headerHeight;
      if(this.isToggleNav && window.innerWidth >= 990) {
        const navHeight = this.nav.clientHeight;
        document.body.style.setProperty('--nav-height', `${navHeight}px`);
        if(this.header.classList.contains('shopify-section-header-sticky') && !this.header.classList.contains('open-nav')) {
          headerHeight = this.header.querySelector('header').clientHeight + navHeight + 11;
        } else {
          headerHeight = this.header.querySelector('header').clientHeight;
        }
      } else {
        headerHeight = this.header.querySelector('header').clientHeight;
      }
      document.body.style.setProperty('--header-height', `${headerHeight}px`);
      const stickyHeaderHeight = headerHeight + this.getHeaderTopHeight();
      if(window.innerWidth >= 990) {
        document.body.style.setProperty('--sticky-header-height-desktop', `${stickyHeaderHeight}px`);
      } else {
        document.body.style.setProperty('--sticky-header-height-mobile', `${stickyHeaderHeight}px`);
      }
      this.calculateThreshold();
    }

    getHeaderTopHeight() {
      if(!this.headerTop) {
        return 0;
      }
      return this.headerTop.clientHeight;
    }

    onToggleNavClick() {
      document.body.style.setProperty('--nav-height', `${this.nav.clientHeight}px`);
      this.header.classList.toggle('open-nav');
    }

    hide() {
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
      this.closeToggleNav();
    }

    reveal() {
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      if(this.dataset.toggleNav) {
        this.toggleNavButton.removeAttribute('tabindex');
      }
    }

    reset() {
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      this.closeToggleNav();
    }

    closeToggleNav() {
      if(this.dataset.toggleNav) {
        this.toggleNavButton.setAttribute('tabindex', '-1');
        this.header.classList.remove('open-nav');
      }
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach(disclosure => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if(this.searchModal) {
        this.searchModal.close(false);
      }
    }

    setPreventHide(value) {
      this.preventHide = value;
    }

    setPreventOpen(value) {
      this.preventOpen = value;
    }
  });
}


if (!customElements.get('mega-menu-submenu')) {
  customElements.define('mega-menu-submenu', class MegaMenuSubmenu extends HTMLElement {
    constructor() {
      super();
      this.initMegaMenuSubMenu()
    }

    /**
     * header-megamenu-sub-menu.liquid
     */
    initMegaMenuSubMenu() {
      let firstShow = !!this.querySelector('.link-content-inner.default-show')
      this.querySelectorAll('.list-menu__item--level2').forEach(a => {
        a.addEventListener('click', this.onSubMenuItemClick.bind(this))
        if (!firstShow) {
          const content = this.querySelector(`.link-content-inner[data-trigger="${a.dataset.trigger}"]`)
          if (content) {
            a.classList.add('current')
            content.classList.add('show')
            firstShow = true
          }
        }
      })
    }

    onSubMenuItemClick(e) {
      const container = this
      let clickElement = e.target
      if (e.target.tagName !== 'A') {
        clickElement = e.target.closest('a')
      }
      const current = container.querySelector('.list-menu__item--level2.current')
      if (current !== clickElement) {
        if (current) {
          current.classList.remove('current')
        }
        clickElement.classList.add('current')
        const currentShow = container.querySelector('.link-content-inner.show')
        if (currentShow) {
          currentShow.classList.remove('show')
        }
        const willShow = container.querySelector(`.link-content-inner[data-trigger="${clickElement.dataset.trigger}"]`)
        if (willShow) {
          willShow.classList.add('show')
          e.preventDefault()
        }
      }
    }
  })
}



if (!customElements.get('mega-tab-collection')) {
  class MegaTabCollection extends HTMLElement {
    constructor() {
      super()

      this.tabItems = {}
      this.querySelectorAll('.tab-collection__left ul li').forEach(item => {
        this.tabItems[item.dataset.collectionId] = item
      })
      this.productPanels = {}
      this.querySelectorAll('.tab-collection__tab-content').forEach(panel => {
        this.productPanels[panel.dataset.collectionId] = panel
      })

      this.sidebar = this.querySelector('.tab-collection__sidebar')
      this.promotions = {}
      this.querySelectorAll('.tab-collection__promotion').forEach(promotion => {
        this.promotions[promotion.dataset.collectionId] = promotion
      })

      this.onTabItemMouseEnterListener = this.onTabItemMouseEnter.bind(this)
    }

    connectedCallback() {
      Object.values(this.tabItems).forEach(item => {
        item.addEventListener('mouseenter', this.onTabItemMouseEnterListener)
        item.addEventListener('focusin', this.onTabItemMouseEnterListener)
      })

      // Activate the first tab by default
      const firstId = Object.keys(this.tabItems)[0]
      if (firstId) this.activate(firstId)
    }

    disconnectedCallback() {
      Object.values(this.tabItems).forEach(item => {
        item.removeEventListener('mouseenter', this.onTabItemMouseEnterListener)
        item.removeEventListener('focusin', this.onTabItemMouseEnterListener)
      })
      this.tabItems = null
      this.productPanels = null
    }

    onTabItemMouseEnter(e) {
      const item = e.currentTarget
      this.activate(item.dataset.collectionId)
    }

    activate(collectionId) {
      Object.entries(this.tabItems).forEach(([id, item]) => {
        if (id === collectionId) {
          item.setAttribute('active', '')
        } else {
          item.removeAttribute('active')
        }
      })
      Object.entries(this.productPanels).forEach(([id, panel]) => {
        if (id === collectionId) {
          panel.setAttribute('active', '')
        } else {
          panel.removeAttribute('active')
        }
      })
      let hasPromotionActive = false
      Object.entries(this.promotions).forEach(([id, panel]) => {
        if (id === collectionId) {
          panel.setAttribute('active', '')
          hasPromotionActive = true
        } else {
          panel.removeAttribute('active')
        }
      })
      if (hasPromotionActive) {
        this.sidebar.setAttribute('has-promotion', '')
      } else {
        this.sidebar.removeAttribute('has-promotion')
      }
    }
  }

  customElements.define('mega-tab-collection', MegaTabCollection);
}